import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  useAddDwellTimeUptickMutation,
  useEditDwellTimeUptickMutation,
  useDeleteDwellTimeUptickMutation,
  useDwellTimeUpticksQuery,
} from "../../../graphql/generated";
import { showDialog } from "../../../redux/slices/alert/Alert.slice";
import DwellTimeUpticksList, {
  DwellTimeUpticksListProps,
} from "./DwellTimeUpticksList";

type DwellTimeUpticksListContainerProps = Omit<
  DwellTimeUpticksListProps,
  "query" | "deleteMutation" | "onRefresh" | "onSave"
>;

export default function DwellTimeUpticksListContainer(
  props: DwellTimeUpticksListContainerProps,
) {
  const { t } = useTranslation(["business", "common"]);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const addDwellTimeUptickMutation = useAddDwellTimeUptickMutation();
  const editDwellTimeUptickMutation = useEditDwellTimeUptickMutation();

  let refetch = () => {};

  const useDwellTimeUpticksQueryCustom: typeof useDwellTimeUpticksQuery = (
    variables,
    options,
  ) => {
    const entityFilter = { businessEntityId: props.businessEntityId };
    const providedFilter = variables?.filter;
    const mergedFilter =
      providedFilter && Object.keys(providedFilter).length
        ? { $and: [entityFilter, providedFilter] }
        : entityFilter;

    const query = useDwellTimeUpticksQuery(
      {
        ...variables,
        filter: mergedFilter,
      },
      {
        ...options,
      },
    );
    refetch = query.refetch;
    return query;
  };

  useDwellTimeUpticksQueryCustom.fetcher = useDwellTimeUpticksQuery.fetcher;
  useDwellTimeUpticksQueryCustom.getKey = useDwellTimeUpticksQuery.getKey;

  const handleSave = async (dwellTimeUptick: any, uptickId?: string) => {
    try {
      if (dwellTimeUptick.addedTimeInMinutes < 1) {
        dispatch(
          showDialog({
            title: t("common:error.title", "Error"),
            type: "error",
            description: t(
              "error.addedTimeInMinutesLessThanOne",
              "Added time in minutes must be at least 1 minute",
            ),
          }),
        );
        return;
      }

      if (uptickId) {
        // Edit existing uptick
        await editDwellTimeUptickMutation.mutateAsync({
          id: uptickId,
          editDwellTimeUptickData: dwellTimeUptick,
        });

        dispatch(
          showDialog({
            title: t("common:success.title", "Success"),
            type: "primary",
            description: t(
              "business:success.dwellTimeUptickUpdated",
              "Dwell Time Uptick updated successfully",
            ),
          }),
        );
      } else {
        // Add new uptick
        await addDwellTimeUptickMutation.mutateAsync({
          newDwellTimeUptickData: dwellTimeUptick,
        });

        dispatch(
          showDialog({
            title: t("common:success.title", "Success"),
            type: "primary",
            description: t(
              "business:success.dwellTimeUptickAdded",
              "Dwell Time Uptick added successfully",
            ),
          }),
        );
      }

      await queryClient.invalidateQueries({
        queryKey: ["dwellTimeUpticks"],
      });

      refetch();
    } catch (error) {
      console.error(error);
      dispatch(
        showDialog({
          title: t("common:error.title", "Error"),
          type: "error",
          description:
            t(
              uptickId
                ? "error.editDwellTimeUptickError"
                : "error.addDwellTimeUptickError",
              uptickId
                ? "An error occurred while updating the dwell time uptick."
                : "An error occurred while adding the dwell time uptick.",
            ) +
            " " +
            ((error as Error).message ||
              t("common:error.unknownError", "Unknown error")),
        }),
      );
    }
  };

  return (
    <DwellTimeUpticksList
      query={useDwellTimeUpticksQueryCustom}
      deleteMutation={useDeleteDwellTimeUptickMutation}
      onRefresh={() => refetch()}
      onSave={handleSave}
      {...props}
    />
  );
}
